import { useHudStore } from "../store/hudStore";
import { stepShare, titleCase, isoToday } from "../data/seed";
import { activeSteps, parseStep } from "../lib/cadence";
import Page from "../components/Page";
import { Badge, Button, Progress } from "../design-system/components";

const pad = (n: number): string => (n < 10 ? "0" : "") + n;

// Focus timer (app.html screenFocus, 903–925). Pomodoro ring over 25 min.
export default function Focus() {
  const st = useHudStore();
  const q = st.quests.find((x) => x.id === st.focusId) || st.quests[0];
  const clock = Math.floor(st.secs / 60) + ":" + pad(st.secs % 60);
  const offset = 703.7 * (1 - Math.min(1, (st.secs % 1500) / 1500));
  const done = q ? !!st.done[q.id] : false;
  const steps = q?.steps || [];
  const qid = q?.id ?? "";
  // Focus is for now: only the steps in their month are offered (lib/cadence.ts).
  const active = activeSteps(steps, isoToday().slice(0, 7));
  const stepsDone = active.filter((i) => done || !!st.stepDone[qid + ":" + i]).length;

  return (
    <Page back="Leave focus" onBack={st.exitFocus}>
      <div className="focus-panel" style={{ display: "flex", flexDirection: "column", alignItems: "center", borderRadius: 12, border: "1px solid var(--line2)", background: "var(--card)", textAlign: "center" }}>
        <Badge tone="accent">In focus</Badge>
        <h1 className="detail-heading" style={{ fontFamily: "var(--display)", fontSize: "clamp(26px,4.2vw,38px)", fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.15, margin: 0, maxWidth: "20ch", textWrap: "pretty" }}>{q ? q.title : "Focus session"}</h1>
        <div className="focus-clock" style={{ position: "relative", aspectRatio: "1", display: "grid", placeItems: "center" }}>
          <svg aria-hidden="true" viewBox="0 0 260 260" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <circle cx="130" cy="130" r="112" fill="none" stroke="var(--raise)" style={{ strokeWidth: 12 }} />
            <circle cx="130" cy="130" r="112" fill="none" stroke="var(--accent)" transform="rotate(-90 130 130)" style={{ strokeWidth: 12, strokeLinecap: "round", strokeDasharray: 703.7, transition: "stroke-dashoffset 1s linear", strokeDashoffset: offset }} />
          </svg>
          <div style={{ position: "relative", fontFamily: "var(--mono)", fontSize: "clamp(40px,9vw,58px)", fontWeight: 700, letterSpacing: 0, fontVariantNumeric: "tabular-nums" }}>{clock}</div>
        </div>
        <div className="focus-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
          <Button onClick={st.toggleTimer} icon={st.running ? "fa-solid fa-pause" : "fa-solid fa-play"}>{st.running ? "Pause" : "Resume"}</Button>
          <Button variant="primary" onClick={st.completeFocus} icon="fa-solid fa-check">Complete · +{q ? q.xp : 0} XP</Button>
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--mut)" }}>{q ? q.domain + " · " + titleCase(q.type) : ""}</div>

        {/* task breakdown: tick tasks off without leaving focus */}
        {q && active.length > 0 && (
          <div style={{ width: "100%", maxWidth: 440, textAlign: "left", display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
            <div className="focus-steps-heading">
              <h2 className="detail-heading" style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 700, color: "var(--sub)" }}>Task breakdown</h2>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--accent-ink)" }}>{stepsDone} / {active.length}</span>
              <Progress className="focus-steps-progress" value={(stepsDone / Math.max(1, active.length)) * 100} aria-label="Task breakdown progress" />
            </div>
            {active.map((i) => {
              const text = parseStep(steps[i] ?? "").label;
              const sd = done || !!st.stepDone[q.id + ":" + i];
              return (
                <button key={i} type="button" className="ds-focusable" onClick={() => st.toggleStep(q.id, i)} disabled={sd} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", minHeight: 44, padding: "12px", borderRadius: 8, background: "var(--card2)", border: "1px solid var(--control-edge)", cursor: sd ? "default" : "pointer" }}>
                  <span style={{ width: 20, height: 20, flex: "0 0 auto", display: "grid", placeItems: "center", borderRadius: sd ? 6 : 999, border: sd ? "1.6px solid transparent" : "1.6px solid var(--control-edge)", background: sd ? "var(--accent)" : "transparent" }}>
                    <i aria-hidden="true" className="fa-solid fa-check" style={{ fontSize: 10, lineHeight: 1, color: "var(--on-accent)", opacity: sd ? 1 : 0 }} />
                  </span>
                  <span className="detail-wrap" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: sd ? "var(--mut)" : "var(--sub)", textDecoration: sd ? "line-through" : "none" }}>{text}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, flex: "0 0 auto", color: sd ? "var(--mut)" : "var(--green-ink)" }}>+{stepShare(q, active, i)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Page>
  );
}
